import { stripLanguagePrefix } from '../i18n';
import { isMeidaisaiRoute } from '../config/meidaisaiDemo';

const FALLBACK_GA_MEASUREMENT_ID = 'G-PGMVB81E3M';

const SESSION_ID_KEY = 'research_analytics_session_id';
const LANDING_UI_MODE_KEY = 'research_analytics_landing_ui_mode';
const PARTICIPANT_STORAGE_KEY = 'research_participant_shop_id';
const PARTICIPANT_QUERY_KEYS = ['shop_id', 'participant_id', 'research_shop'];

function isPlaceholder(value) {
  return typeof value === 'string' && value.startsWith('%VITE_') && value.endsWith('%');
}

export function getMeasurementId() {
  const envId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!envId || isPlaceholder(envId)) {
    return FALLBACK_GA_MEASUREMENT_ID;
  }
  return envId;
}

export function isAnalyticsEnabled() {
  // ローカル開発（vite dev）は常に無効
  if (import.meta.env.DEV) {
    return false;
  }

  // VITE_DISABLE_ANALYTICS=true が明示的に設定されている場合（staging等）は無効
  if (import.meta.env.VITE_DISABLE_ANALYTICS === 'true') {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  // 名大祭デモ（本番相当UI・ログなし体験）
  if (isMeidaisaiRoute(window.location.pathname)) {
    return false;
  }

  // vercel.app ドメインからのアクセスは開発・ステージング環境とみなして無効
  // 本番は独自ドメイン（ai-camera.lab.mdg-meidai.com）経由でのみ計測する
  if (window.location.hostname.endsWith('.vercel.app')) {
    return false;
  }

  return true;
}

/**
 * 言語プレフィックスを除いたパスから purpose / function / other を返す
 */
export function getDashboardUiMode(pathname) {
  const p = stripLanguagePrefix(pathname || '');
  if (p === '/' || p === '/purpose') return 'purpose';
  if (p === '/function') return 'function';
  if (p === '/tv') return 'tv';
  return 'other';
}

export function initAnalytics() {
  if (!isAnalyticsEnabled()) return;

  const measurementId = getMeasurementId();

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag('js', new Date());

  const uid = getParticipantShopId();
  gtag('config', measurementId, {
    ...(uid ? { user_id: uid } : {}),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
}

export function getAnalyticsPage(pathname) {
  const mode = getDashboardUiMode(pathname);
  if (mode === 'other') {
    const p = stripLanguagePrefix(pathname || '');
    return p || 'unknown';
  }
  return mode;
}

function sanitizeParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function getOrCreateSessionId() {
  if (typeof sessionStorage === 'undefined') return '';
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

export function getLandingUiMode() {
  if (typeof sessionStorage === 'undefined') return '';
  return sessionStorage.getItem(LANDING_UI_MODE_KEY) || '';
}

/**
 * タブセッション内で初めて purpose / function に入ったときだけ landing を記録する
 */
function touchResearchSession(pathname) {
  if (typeof sessionStorage === 'undefined') return;
  getOrCreateSessionId();
  if (sessionStorage.getItem(LANDING_UI_MODE_KEY)) return;
  const mode = getDashboardUiMode(pathname);
  if (mode !== 'other') {
    sessionStorage.setItem(LANDING_UI_MODE_KEY, mode);
  }
}

function getResearchContext(pathname) {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  touchResearchSession(path);
  const landing = getLandingUiMode();
  const participant = getParticipantShopId();
  return {
    session_id: getOrCreateSessionId(),
    ...(landing ? { landing_ui_mode: landing } : {}),
    current_ui_mode: getDashboardUiMode(path),
    ...(participant ? { participant_shop_id: participant } : {}),
  };
}

export function getParticipantShopId() {
  const envId = import.meta.env.VITE_RESEARCH_PARTICIPANT_ID;
  if (envId && String(envId).trim()) {
    return String(envId).trim().slice(0, 64);
  }
  if (typeof localStorage === 'undefined') return '';
  return (localStorage.getItem(PARTICIPANT_STORAGE_KEY) || '').trim().slice(0, 64);
}

/**
 * URL クエリから店舗・参加者 ID を取り込み。ヒットしたキーはクエリから削除した文字列を返す（変更なければ null）
 */
export function consumeParticipantIdFromSearch(searchString) {
  if (typeof localStorage === 'undefined') return null;
  const params = new URLSearchParams(searchString || '');
  let found = false;

  for (const key of PARTICIPANT_QUERY_KEYS) {
    const v = params.get(key);
    if (v && String(v).trim()) {
      localStorage.setItem(PARTICIPANT_STORAGE_KEY, String(v).trim().slice(0, 64));
      params.delete(key);
      found = true;
      break;
    }
  }

  if (!found) return null;
  const next = params.toString();
  return next ? `?${next}` : '';
}

export function applyParticipantToGtag() {
  if (!isAnalyticsEnabled() || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  const uid = getParticipantShopId();
  if (!uid) return;
  window.gtag('config', getMeasurementId(), { user_id: uid });
}

export function trackPageView(pagePath) {
  if (!isAnalyticsEnabled()) return;

  const pathForSession = typeof window !== 'undefined' ? window.location.pathname : '';
  touchResearchSession(pathForSession);

  window.gtag('config', getMeasurementId(), {
    page_path: pagePath,
  });
}

export function trackEvent(eventName, params = {}) {
  if (!isAnalyticsEnabled()) return;

  const pathForSession = typeof window !== 'undefined' ? window.location.pathname : '';
  const ctx = getResearchContext(pathForSession);

  window.gtag('event', eventName, sanitizeParams({ ...ctx, ...params }));
}

const TV_VISITOR_KEY = 'tv_visitor';
const TV_CAMPAIGN_KEY = 'tv_campaign';
const TV_FEEDBACK_DONE_KEY = 'tv_feedback_done';
const TV_VISIT_COUNT_KEY = 'tv_visit_count';
const TV_VISIT_COUNTED_KEY = 'tv_visit_counted';
const TV_SURVEY_PROFILE_KEY = 'tv_survey_profile_done';
const TV_SURVEY_FIRST_KEY = 'tv_survey_first_done';
const TV_SURVEY_SECOND_KEY = 'tv_survey_second_done';
const TV_FORM_CARD_DISMISSED_KEY = 'tv_form_card_dismissed';

function getSessionDurationSec() {
  if (typeof performance === 'undefined') return 0;
  const navStart = performance.timing?.navigationStart;
  if (navStart) {
    return Math.round((Date.now() - navStart) / 1000);
  }
  if (typeof performance.now === 'function') {
    return Math.round(performance.now() / 1000);
  }
  return 0;
}

/**
 * UTM /tv 流入を検出し、sessionStorage フラグと GA4 tv_entry を記録する
 */
export function detectAndTrackTVEntry() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmCampaign = params.get('utm_campaign');
  const path = stripLanguagePrefix(window.location.pathname || '');
  const isTvPath = path === '/tv';

  if (utmSource !== 'tv' && !isTvPath) return;

  const alreadyVisitor = sessionStorage.getItem(TV_VISITOR_KEY) === 'true';
  sessionStorage.setItem(TV_VISITOR_KEY, 'true');
  bumpTVVisitCount();

  if (utmSource === 'tv') {
    sessionStorage.setItem(TV_CAMPAIGN_KEY, utmCampaign || '');
  }

  // 初回のテレビ流入（UTM または旧 /tv）で tv_entry を送る
  if (!alreadyVisitor && (utmSource === 'tv' || isTvPath)) {
    trackEvent('tv_entry', {
      utm_source: utmSource || 'tv',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: utmCampaign || '',
      landing_path: window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  }
}

export function isTVVisitor() {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(TV_VISITOR_KEY) === 'true';
}

export function getTVCampaign() {
  if (typeof sessionStorage === 'undefined') return '';
  return sessionStorage.getItem(TV_CAMPAIGN_KEY) || '';
}

function readStorage(storage, key) {
  if (typeof storage === 'undefined' || !storage) return '';
  try {
    return storage.getItem(key) || '';
  } catch {
    return '';
  }
}

function writeStorage(storage, key, value) {
  if (typeof storage === 'undefined' || !storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // private mode など
  }
}

function bumpTVVisitCount() {
  if (readStorage(sessionStorage, TV_VISIT_COUNTED_KEY) === 'true') return;
  writeStorage(sessionStorage, TV_VISIT_COUNTED_KEY, 'true');
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  const current = Number(readStorage(store, TV_VISIT_COUNT_KEY) || 0);
  writeStorage(store, TV_VISIT_COUNT_KEY, String(Number.isFinite(current) ? current + 1 : 1));
}

export function getTVVisitCount() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  const n = Number(readStorage(store, TV_VISIT_COUNT_KEY) || 0);
  return Number.isFinite(n) ? n : 0;
}

export function isTVProfileDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  return readStorage(store, TV_SURVEY_PROFILE_KEY) === 'true';
}

export function markTVProfileDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  writeStorage(store, TV_SURVEY_PROFILE_KEY, 'true');
}

export function isTVFeedbackDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  return readStorage(store, TV_FEEDBACK_DONE_KEY) === 'true'
    || readStorage(sessionStorage, TV_FEEDBACK_DONE_KEY) === 'true';
}

export function markTVFeedbackDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  writeStorage(store, TV_FEEDBACK_DONE_KEY, 'true');
  writeStorage(sessionStorage, TV_FEEDBACK_DONE_KEY, 'true');
}

export function isTVFormCardDismissed() {
  return readStorage(sessionStorage, TV_FORM_CARD_DISMISSED_KEY) === 'true';
}

export function dismissTVFormCard() {
  writeStorage(sessionStorage, TV_FORM_CARD_DISMISSED_KEY, 'true');
}

function isLegacySurveyComplete() {
  return isTVProfileDone() && isTVFeedbackDone();
}

export function isTVFirstSurveyDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  return readStorage(store, TV_SURVEY_FIRST_KEY) === 'true' || isLegacySurveyComplete();
}

export function markTVFirstSurveyDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  writeStorage(store, TV_SURVEY_FIRST_KEY, 'true');
}

export function isTVSecondSurveyDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  return readStorage(store, TV_SURVEY_SECOND_KEY) === 'true' || isLegacySurveyComplete();
}

export function markTVSecondSurveyDone() {
  const store = typeof localStorage === 'undefined' ? undefined : localStorage;
  writeStorage(store, TV_SURVEY_SECOND_KEY, 'true');
}

/**
 * 1回目は5段階評価と属性。2回目は目的と感想。
 * 両方済みなら、右下カードから外部フォームへ誘導する。
 */
export function getTVSurveyMode() {
  if (!isTVVisitor()) return null;
  if (!isTVFirstSurveyDone()) return 'first';
  if (!isTVSecondSurveyDone()) return 'second';
  if (isTVFormCardDismissed()) return null;
  return 'link';
}

/**
 * TV訪問者のみの行動深度イベント（tv_user_*）
 */
export function trackTVUserAction(actionName, additionalParams = {}) {
  if (!isTVVisitor()) return;
  trackEvent(`tv_user_${actionName}`, {
    ...additionalParams,
    session_duration_sec: getSessionDurationSec(),
    campaign: getTVCampaign(),
  });
}

export function trackTVFeedback(params = {}) {
  trackEvent('tv_feedback', {
    ...params,
    campaign: getTVCampaign(),
  });
}
